/* eslint-disable */
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown, Download, SearchIcon
} from 'lucide-react';
import React, {
    useCallback, useEffect, useMemo, useState
} from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Input, InputGroup } from 'rsuite';
import { urls } from '@/Constants';
import { inputHelper, keyPressHandler } from '@/Helpers';
import { tabs } from './tabs.constants';
import GrnHeader from '../GRNPage/grnHeader';
import SortComp from './SortComp';
import TableWithTabs from './TableWithTabs';
import Info from '@/Components/InfoTab/Info';
import useKeyPress from '@/Helpers/useKeyPress';
import moment from 'moment';
import { useSessionStorageList } from './helper';

const SORT_OPTIONS = [
    { value: 'name', label: 'Item Name' },
    { value: 'orderedQty', label: 'Ordered Qty' },
    { value: 'dispatchQty', label: 'Dispatched Qty' },
    { value: 'leftOverQty', label: 'Left Over Qty' },
    { value: 'consumedQty', label: 'Consumed Qty' },
];

const SORT_COST_OPTIONS = [
    { value: 'name', label: 'Item Name' },
    { value: 'orderCost', label: 'Ordered Amount' },
    { value: 'dispatchedCost', label: 'Dispatched Amount' },
    { value: 'leftOverCost', label: 'Left Over Amount' },
    { value: 'consumedCost', label: 'Consumed Amount' },
];

const WEIGHT_BASED_COLUMNS = [
    { title: 'Item Name', key: 'name' },
    { title: 'Price per Unit', key: 'pricePerUnit' },
    { title: 'Ordered Qty', key: 'orderedQty' },
    { title: 'Dispatch Qty', key: 'dispatchQty' },
    { title: 'Left Over Qty', key: 'leftOverQty' },
    { title: 'Consumed Qty', key: 'consumedQty' },
];

const COST_BASED_COLUMNS = [
    { title: 'Item Name', key: 'name' },
    { title: 'Price per Unit', key: 'pricePerUnit' },
    { title: 'Ordered Amount', key: 'orderCost' },
    { title: 'Dispatched Amount', key: 'dispatchedCost' },
    { title: 'Left Over Amount', key: 'leftOverCost' },
    { title: 'Consumed Amount', key: 'consumedCost' },
];

const FIELD_MAP = {
    'Section Location': 'podName',
    'Ordered Quantity': 'totalOrderedQty',
    'Dispatched Quantity': 'totalDispatchedQty',
    'Left Over Quantity': 'totalLeftOverQty',
    'Consumed Quantity': 'totalConsumedQty',
    'Ordered Amount': 'totalOrderedCost',
    'Dispatched Amount': 'totalDispatchedCost',
    'Left Over Amount': 'totalLeftOverCost',
    'Consumed Amount': 'totalConsumedCost',
};

const TABLE_TAB_CONFIG = {
    tabList: [{
        id: 1,
        value: 'ingredient',
        label: 'Ingredient',
    }],
    activeTabColor: 'bg-yellow-200',
};

const extractNumeric = (text) => {
    if (!text) return NaN;
    const num = parseFloat(String(text).replace(/[^\d.-]/g, ''));
    return isNaN(num) ? NaN : num;
};

const getTextFromElement = (element) => {
    if (!React.isValidElement(element)) return element;
    const { children } = element.props;

    if (Array.isArray(children)) {
        return children.map(getTextFromElement).join('');
    } 
    if (React.isValidElement(children)) {
        return getTextFromElement(children);
    }
    return children ?? '';
};

const formatCurrency = (value) => `₹${parseFloat(value)?.toFixed(2)}`;
const formatQuantity = (value, unit) => `${parseFloat(value)?.toFixed(2)} ${unit}`;

const useProductData = (list, id) => {
    const [productData, setProductData] = useState({});
    const [idx, setIdx] = useState(-1);

    useEffect(() => {
        if (list?.length > 0) {
            const index = list?.findIndex((item) => item?.headerInfo?.podId == id);
            const obj = index !== -1 ? list[index] : null;
            setProductData(obj);
            setIdx(index);
        }
    }, [list, id]);

    return { productData, idx };
};

function RestaurantDetailComp({ name, id, filters, meal, rowIndex }) {
    const [uiState, setUiState] = useState({
        selectedTab: tabs[0]?.id,
        tableTab: 'ingredient',
        search: '',
        isSortOpen: false,
		sortVal: {},
    });
    
    const history = useHistory();
    const keyPressed = useKeyPress();
    const list = useSessionStorageList();
    const { productData, idx } = useProductData(list, id);

    const backUrl = useMemo(() => {
		return `${urls.EXTENSION}${urls.AI_FMS_PAGE}?parentId=1&subTab=INDENT&mealType=${meal}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPodFilter=${filters?.fromPodFilter}&rowIndex=${idx}`;
	}, [meal, filters, idx]);

    const productInfo = useMemo(() => {
        const headerInfo = productData?.headerInfo;
        if (!headerInfo) return {};
        
        return {
            id: headerInfo?.podId,
            podName: headerInfo?.podName,
            totalOrderedQty: formatQuantity(headerInfo?.orderedQty, headerInfo?.unit),
            totalDispatchedQty: formatQuantity(headerInfo?.dispatchedQty, headerInfo?.unit),
            totalLeftOverQty: formatQuantity(headerInfo?.leftOverQty, headerInfo?.unit),
            totalConsumedQty: headerInfo?.consumedQty > 0 ? formatQuantity(headerInfo?.consumedQty, headerInfo?.unit) : `0.00 ${headerInfo?.unit}`,
            totalOrderedCost: formatCurrency(headerInfo?.orderedCost),
            totalDispatchedCost: formatCurrency(headerInfo?.dispatchedCost),
            totalLeftOverCost: formatCurrency(headerInfo?.leftOverCost),
            totalConsumedCost: headerInfo?.consumedCost > 0 ? formatCurrency(headerInfo?.consumedCost) : '₹0.00',
        };
    }, [productData?.headerInfo]);

    const handleExport = useCallback(() => {
        const isWeightTab = uiState.selectedTab === 'weight';
        const headers = isWeightTab ? [
            'Meal Name', 'Export Type', 'Item Name', 'Price per Unit',
            'Ordered Qty', 'Dispatched Qty', 'Left Over Qty', 'Consumed Qty',
        ] : [
            'Meal Name', 'Export Type', 'Item Name', 'Price per Unit',
            'Ordered Amount', 'Dispatched Amount', 'Left Over Amount', 'Consumed Amount',
        ];

        const data = productData?.list?.map((item) => isWeightTab ? [
            meal ?? '-',
            'Restaurant Indent',
            item?.ingName,
            `${item?.rate}/${item?.unit}`,
            formatQuantity(item?.orderQty, item?.unit),
            formatQuantity(item?.fullfilledQty, item?.unit),
            formatQuantity(item?.leftOverQty, item?.unit),
            formatQuantity(item?.consumptionQty, item?.unit),
        ] : [
            meal ?? '-',
            'Restaurant Indent',
            item?.ingName,
            `${item?.rate}/${item?.unit}`,
            item?.orderQty * item?.rate,
            item?.fullfilledCost,
            item?.leftOverQty * item?.rate,
            item?.consumptionQty * item?.rate,
        ]);

        const csvData = [headers, ...data];
        const csvContent = csvData.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${productData?.headerInfo?.podName}${filters?.dateRange ? moment(filters?.dateRange?.from).format('DD-MM-YYYY') : ''}-${filters?.dateRange ? moment(filters?.dateRange?.to).format('DD-MM-YYYY') : ''}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }, [uiState.selectedTab, productData, meal, filters]);

    const buttons = useMemo(() => [
        {
            label: 'Export',
            icon: <Download size={16} />,
            handleClick: handleExport,
            noLevelSpecific: true,
            style: 'bg-teal-600 text-white',
        },
    ], [handleExport]);

    const updateUiState = useCallback((updates) => {
        setUiState(prev => ({ ...prev, ...updates }));
    }, []);

    const handleSortChange = useCallback((value, type) => {
        setUiState({
			...uiState,
            sortVal: {
                sortColumnName: value,
                sortType: type,
            },
        });
    }, [uiState?.selectedTab]);

    const navigateQc = useCallback((e) => {
        const { dataset } = inputHelper(e);
        let newListItem = null;

        if (dataset.type === 'next') {
            newListItem = list?.[idx + 1];
        } else {
            newListItem = list?.[idx - 1];
        }

        if (newListItem) {
            history.push({
                pathname: `${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}`,
                search: `mode=restaurantIndent&id=${newListItem?.headerInfo?.podId}&name=${newListItem?.headerInfo?.podName}&meal=${meal}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${idx}`,
            });
        }
    }, [list, idx, history, meal, filters]);

    useEffect(() => {
        const newListItem = list?.[idx] ?? null;
        if (
            (keyPressed === 'ArrowLeft' || keyPressed === 'ArrowRight') &&
            newListItem !== null
        ) {
            const targetItem = keyPressHandler(keyPressed, list, idx);
            if (targetItem) {
                history.push({
                    pathname: `${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}`,
                    search: `mode=restaurantIndent&id=${targetItem?.headerInfo?.podId}&name=${targetItem?.headerInfo?.podName}&meal=${meal}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${idx}`,
                });
            }
        }
    }, [keyPressed]);

    const generatedData = useCallback(
        (list, customRowData = {}) => {
            const { sortColumnName: sortCol, sortType } = uiState?.sortVal;

            let result = list ? [...list] : [];

            if (sortCol && sortType && customRowData[sortCol]) {
                const renderFn = customRowData[sortCol];
                result.sort((a, b) => {
                    const aVal = getTextFromElement(renderFn(a));
                    const bVal = getTextFromElement(renderFn(b));

                    const numA = extractNumeric(aVal);
                    const numB = extractNumeric(bVal);
                    
                    if (!isNaN(numA) && !isNaN(numB)) {
                        return sortType === 'ascending' ? numA - numB : numB - numA;
                    }

                    return sortType === 'ascending'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal));
                });
            }

            const searchTerm = uiState.search?.toLowerCase()?.trim();
            if (searchTerm) {
                result = result.filter(
                    (item) => item?.ingName?.toLowerCase()?.trim()?.includes(searchTerm) ||
                        item?.itemClientVariantId?.toLowerCase()?.trim()?.includes(searchTerm)
                );
            }

            return result;
        },
        [uiState.search, uiState?.sortVal]
    );

    const createRowRenderer = useCallback((field, formatter) => (product) => (
        <span className='text-[12px]'>{formatter(product)}</span>
    ), []);

    const getWeightBasedTable = useMemo(() => {
        const customRowData = {
            name: createRowRenderer('name', (product) => product?.ingName),
            pricePerUnit: createRowRenderer('pricePerUnit', (product) => `${product?.rate}/${product?.unit}`),
            orderedQty: createRowRenderer('orderedQty', (product) => `${parseFloat(product?.orderQty)?.toFixed(2)} ${product?.unit}`),
            dispatchQty: createRowRenderer('dispatchQty', (product) => `${parseFloat(product?.fullfilledQty)?.toFixed(2)} ${product?.unit}`),
            leftOverQty: createRowRenderer('leftOverQty', (product) => `${parseFloat(product?.leftOverQty)?.toFixed(2)} ${product?.unit}`),
            consumedQty: createRowRenderer('consumedQty', (product) => product?.consumptionQty > 0 ? `${parseFloat(product?.consumptionQty)?.toFixed(2)} ${product?.unit}` : 0),
        };

        return {
            data: generatedData(productData?.list, customRowData),
            tableRows: customRowData,
            tableColumns: WEIGHT_BASED_COLUMNS,
        };
    }, [productData?.list, generatedData, createRowRenderer]);

    const getCostBasedTable = useMemo(() => {
        const customRowData = {
            name: createRowRenderer('name', (product) => product?.ingName),
            pricePerUnit: createRowRenderer('pricePerUnit', (product) => `₹${product?.rate}/${product?.unit}`),
            orderCost: createRowRenderer('orderCost', (product) => formatCurrency(product?.orderQty * product?.rate)),
            dispatchedCost: createRowRenderer('dispatchedCost', (product) => formatCurrency(product?.fullfilledCost)),
            leftOverCost: createRowRenderer('leftOverCost', (product) => formatCurrency(product?.leftOverQty * product?.rate)),
            consumedCost: createRowRenderer('consumedCost', (product) => product?.consumptionQty > 0 ? formatCurrency(product?.consumptionQty * product?.rate) : '₹0.00'),
        };

        return {
            data: generatedData(productData?.list, customRowData),
            tableRows: customRowData,
            tableColumns: COST_BASED_COLUMNS,
        };
    }, [productData?.list, generatedData, createRowRenderer]);

    const getTableInfo = useCallback(() => {
        return uiState.selectedTab === 'weight' ? getWeightBasedTable : getCostBasedTable;
    }, [uiState.selectedTab, getWeightBasedTable, getCostBasedTable]);

    const getTabInfo = useMemo(() => ({
        ...TABLE_TAB_CONFIG,
        tabValue: uiState.tableTab,
        handleTabChange: (value) => updateUiState({ tableTab: value}),
    }), [uiState.tableTab, updateUiState]);

    const filteredTabs = useMemo(() => {
        return tabs?.filter((item) => item?.id !== 'percentage');
    }, []);

    const getSortType = (columnName, sortType) => {
        if (columnName) {
                const name = uiState.selectedTab === 'weight' ? SORT_OPTIONS.find(option => option.value === columnName)?.label : SORT_COST_OPTIONS.find(option => option.value === columnName)?.label;
                const sortTypeIcon = sortType === 'ascending' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
            return <span className='flex items-start gap-1'> <strong className='flex items-center gap-1'> <ArrowUpDown size={16} /> Sort By:</strong> {name} {sortTypeIcon}</span>;
        }
        return <span className='flex items-start gap-1'> <ArrowUpDown size={16} /> Sort By</span>;
    };

    return (
        <>
            <GrnHeader
                title='Back to Operations Summary'
                navigateLabel='Section Name'
                id={name}
                navigatePrevToolTip='Prev Section'
                navigateNextToolTip='Next Section'
                navigateQC={navigateQc}
                listData={list}
                idx={idx}
                url={backUrl}
            />
            <div className='relative top-[3rem] p-4'>
                <Info
                    buttons={buttons}
                    fieldMap={FIELD_MAP}
                    data={productInfo}
                    heading={{ key: name }}
                />
                <div className='flex justify-between items-center'>
                    <div className='w-fit inline-flex rounded-lg bg-[#FDE6AD] p-1 m-[12px]'>
                        {filteredTabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => updateUiState({ selectedTab: tab.id, sortVal: {} })}
                                className={`px-4 py-2 flex items-center text-sm font-medium transition-all duration-200 rounded-md ${
                                    uiState.selectedTab === tab.id
                                        ? 'bg-yellow-400 text-black shadow-sm'
                                        : 'text-gray-700 hover:bg-yellow-300/50'
                                }`}
                            >
                                <span className='mr-2'>{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className='flex gap-2 items-center'>
                        <InputGroup
                            inside
                            className='rounded-full !bg-gray-200 border border-gray-200 px-2 py-1 !w-[350px]'
                        >
                            <InputGroup.Button
                                className='!bg-transparent !shadow-none pointer-events-none'
                                style={{
                                    background: 'transparent', 
                                    boxShadow: 'none', 
                                    cursor: 'default',
                                }}
                            >
                                <SearchIcon size={16} className='text-gray-500' />
                            </InputGroup.Button>
                            <Input
                                placeholder='Search by Item Name'
                                className='bg-transparent border-none focus:outline-none'
                                style={{
                                    background: 'transparent', 
                                    border: 'none',
                                }}
                                value={uiState.search}
                                onChange={(value) => updateUiState({ search: value })}
                            />
                        </InputGroup>
                        <div className='relative'>
                            <button
                                onClick={() => updateUiState({ isSortOpen: true })}
                                className='px-4 py-2 flex items-center text-sm font-medium transition-all duration-200 rounded-md bg-yellow-400 text-black shadow-sm'
                            >
                                {getSortType(uiState?.sortVal?.sortColumnName, uiState?.sortVal?.sortType)}
                            </button>
                            <SortComp
                                isOpen={uiState.isSortOpen}
                                setIsOpen={(isOpen) => updateUiState({ isSortOpen: isOpen })}
                                onSortChange={handleSortChange}
                                sortOptions={uiState.selectedTab === 'weight' ? SORT_OPTIONS : SORT_COST_OPTIONS}
                            />
                        </div>
                    </div>
                </div>
                <TableWithTabs
                    tableInfo={getTableInfo()}
                    tabInfo={getTabInfo}
                />
            </div>
        </>
    );
}

export default React.memo(RestaurantDetailComp);
